import { CanalNotificacao } from '../../enum/parametros/canal-notificacao.enum';
import { TipoInstituto } from '../../enum/parametros/tipo-instituto.enum';
import { CondicaoRegra, OpcaoCondicao, ParametroCondicao } from '../../models/parametros/extrato-desligamento.model';

/** Textos da condição parametrizável na tela; a faixa de valores vem do catálogo. */
type TextosParametro = Omit<ParametroCondicao, 'minimo' | 'maximo'>;

/** Textos das condições parametrizáveis conhecidas, pelo `nome` do catálogo. */
const TEXTOS_PARAMETRO_CONDICAO: Readonly<Record<string, TextosParametro>> = {
  TEMPO_MIN_VINCULACAO: {
    prefixo: 'Mínimo de',
    sufixo: 'anos de vinculação ao plano',
    unidade: 'anos',
    rotulo: 'Anos de vinculação',
    padrao: 3,
    descrever: (anos) => `Mínimo de ${anos} ${anos === 1 ? 'ano' : 'anos'} de vinculação`,
  },
};

/** Faixa usada quando o catálogo não informa os limites (QTD_PARAMETRO NUMBER(3) > 0). */
const PARAMETRO_MINIMO_PADRAO = 1;
const PARAMETRO_MAXIMO_PADRAO = 999;

/**
 * Valor pedido por uma condição parametrizável: os textos conhecidos pelo `nome` do catálogo ou,
 * para uma condição nova, textos genéricos com o nome de exibição.
 */
export function parametroCondicao(
  nome: string,
  nomeExibicao: string,
  minimo: number | null | undefined,
  maximo: number | null | undefined,
): ParametroCondicao {
  const faixa = { minimo: minimo ?? PARAMETRO_MINIMO_PADRAO, maximo: maximo ?? PARAMETRO_MAXIMO_PADRAO };
  const textos = TEXTOS_PARAMETRO_CONDICAO[nome.trim().toUpperCase()];
  if (textos) {
    return { ...textos, ...faixa, padrao: Math.min(Math.max(textos.padrao, faixa.minimo), faixa.maximo) };
  }
  return {
    prefixo: nomeExibicao,
    sufixo: '',
    unidade: '',
    rotulo: nomeExibicao,
    padrao: faixa.minimo,
    ...faixa,
    descrever: (valor) => `${nomeExibicao}: ${valor}`,
  };
}

/** Texto da condição na regra, com o valor quando ela é parametrizável. */
export function descreverCondicao(condicao: CondicaoRegra, opcoes: readonly OpcaoCondicao[]): string {
  const opcao = opcoes.find((item) => item.value === condicao.idCondicao);
  if (!opcao) {
    return String(condicao.idCondicao);
  }
  return opcao.parametro && condicao.valor !== undefined ? opcao.parametro.descrever(condicao.valor) : opcao.label;
}

/** Nome e descrição de cada instituto no extrato. */
export const INSTITUTOS_EXTRATO_INFO: Readonly<Record<TipoInstituto, { nome: string; descricao: string }>> = {
  [TipoInstituto.Resgate]: {
    nome: 'Resgate',
    descricao: 'Permite o resgate dos recursos, conforme as regras do plano.',
  },
  [TipoInstituto.Autopatrocinio]: {
    nome: 'Autopatrocínio',
    descricao: 'Permite a continuidade da contribuição pelo participante.',
  },
  [TipoInstituto.Portabilidade]: {
    nome: 'Portabilidade',
    descricao: 'Permite a transferência dos recursos para outra entidade.',
  },
  [TipoInstituto.BeneficioProporcionalDiferido]: {
    nome: 'Benefício Proporcional Diferido (BPD)',
    descricao: 'Mantém os recursos no plano até a elegibilidade ao benefício.',
  },
};

/** Tamanho máximo da mensagem de não elegibilidade. */
export const MENSAGEM_NAO_ELEGIBILIDADE_MAXIMO = 300;

/** Quando o participante pode ser notificado: dias antes do vencimento do prazo de adesão. */
export const ANTECEDENCIAS_NOTIFICACAO_DIAS: readonly number[] = [30, 15, 5];

/** Canais de envio das notificações, na ordem da tela. */
export const CANAIS_NOTIFICACAO_OPCOES: readonly { value: CanalNotificacao; label: string }[] = [
  { value: CanalNotificacao.Email, label: 'E-mail' },
  { value: CanalNotificacao.PortalParticipante, label: 'Portal do Participante' },
  { value: CanalNotificacao.Sms, label: 'SMS' },
];
